/**
 *
 * HOW TO BUILD the .d.ts file for types in monaco editor:
 *
 * 0. terminate the `yarn start` webpack dev server in the vscode terminal (or wherever it was started from), as it may crash in step 2
 *
 * 1. cd to editor folder: `cd /home/gui/work/web/polygonjs/dev/polygonjs-editor-rails/app/javascript/polygonjs-editor`
 *
 * 2. move @polygonjs to the compilation folder: `yarn move_engine_to_compilation_folder`
 *
 * 3. cd to the compilation folder: `cd /home/gui/work/web/polygonjs/build/polygonjs`
 *
 * 4. run step1: `yarn bundle_types_step1`
 *
 * 5. copy errors displayed in shell to `config/dts_bundle/errors_detected.ts`
 *
 * 6. run step2: `yarn bundle_types_step2`
 *
 * 7. keep copy of current bundled_types: `cp ~/work/web/polygonjs/dev/polygonjs-editor-rails/public/editor/codenodes/bundledTypes.d.ts.edited.txt ~/work/web/polygonjs/dev/polygonjs-editor-rails/public/editor/codenodes/bundledTypes.d.ts.edited.txt.bak`
 *
 * 8. copy output file to rails editor: `cp public/bundled_types.d.ts ~/work/web/polygonjs/dev/polygonjs-editor-rails/public/editor/codenodes/bundledTypes.d.ts.edited.txt`
 *
 * 9. bring @polygonjs back, by running: `cd /home/gui/work/web/polygonjs/dev/polygonjs-editor-rails/app/javascript/polygonjs-editor && yarn move_engine_back_from_failed_build`
 *
 * 10. restart webpack dev server `yarn start`
 *
 * 11. display in console the errors when parsing that file, by setting the variable DEBUG_EXTRA_LIB below to true.
 *
 * 12. fix the remaining errors manually. it may be useful to copy elements from the .bak file (mostly what replaces the imports at the top of the file). Some "// @ts-ignore" or conversion to any will be necessary. There will also be remaining errors, such as:
 * - All declarations of 'target' must have identical modifier
 * - Subsequent property declarations must have the same type.  Property 'target'
 * - Type 'EventListener' is not generic
 * Those are safe to ignore, and if they are the only ones left, go to next step.
 *
 * 13. opening a code node should not have typescript errors, or words underlined in red anymore.
 *
 * 14. copy the rails file to the editor repo: `cp ~/work/web/polygonjs/dev/polygonjs-editor-rails/public/editor/codenodes/bundledTypes.d.ts.edited.txt ~/work/web/polygonjs/dev/polygonjs-editor-rails/app/javascript/polygonjs-editor/public/editor/codenodes/bundledTypes.d.ts.edited.txt`
 */

import {BaseNodeType} from '../../../../../../@polygonjs/polygonjs/src/engine/nodes/_Base';
import * as monaco from 'monaco-editor';
import {FilesRegister} from '../../../../self_contained/FilesRegister';
import {CoreString} from '../../../../../../@polygonjs/polygonjs/src/core/String';
import {StringParam} from '../../../../../../@polygonjs/polygonjs/src/engine/params/String';
import {ParamSetCommand} from '../../../../history/commands/ParamSet';
import {MultipleCommand} from '../../../../history/commands/Multiple';
import {MonacoCommonOptions, updateMonacoEditorModel, monacoEditorCreateModel} from './MonacoEditorCommon';
import {StringParamLanguage} from '../../../../../../@polygonjs/polygonjs/src/engine/params/utils/OptionsController';

const DEBUG_EXTRA_LIB = false;
interface SetupForTsOptions {
	readOnly?: boolean;
	uniqueId: string;
}
export class MonacoTypescriptController {
	private static _libsAdded: boolean = false;
	private static model: monaco.editor.ITextModel | null = null;
	constructor() {}

	static updateModel(content: string) {
		updateMonacoEditorModel(content, this.model);
	}

	static async saveParam(param: StringParam, multiCmd: MultipleCommand) {
		const javascriptParam = param.node.params.get('codeJavascript') as StringParam;
		if (javascriptParam && this.model) {
			const transpiled = (await MonacoTypescriptController.transpile(this.model)) || '';
			// if (transpiled) {
			const cmd = new ParamSetCommand(javascriptParam, transpiled);
			multiCmd.pushCommand(cmd);
			// await Editor.instance().pushCommand(cmd);
			// } else {
			// 	param.node.states.error.set('code transpilation failed');
			// 	console.log('set error', param.node.path());
			// }
		}
	}

	static async addLibs() {
		if (this._libsAdded) {
			return;
		}
		this._libsAdded = true;

		// monaco.languages.typescript.typescriptDefaults.setInlayHintsOptions({
		// 	includeInlayParameterNameHints: 'all',
		// 	includeInlayParameterNameHintsWhenArgumentMatchesName: true,
		// 	includeInlayFunctionParameterTypeHints: true,
		// 	includeInlayVariableTypeHints: true,
		// 	includeInlayPropertyDeclarationTypeHints: true,
		// 	includeInlayFunctionLikeReturnTypeHints: true,
		// 	includeInlayEnumMemberValueHints: true,
		// });

		// const url = `/editor/monaco-editor/bundled_types.d.ts?${Date.now()}`;
		// const url = `/bundled_types.d.ts?${Date.now()}`;
		// const response = await fetch(url);
		// const content = await response.text();
	}

	static async setupForTypescriptWithoutNode(
		rawInput: string,
		element: HTMLElement,
		options: SetupForTsOptions,
		param?: StringParam
	) {
		MonacoTypescriptController.setCompilerOptions();

		if (DEBUG_EXTRA_LIB) {
			debugExtraLib();
			return;
		}

		await MonacoTypescriptController.addLibs();
		if (param) {
			await MonacoTypescriptController.addTypesForNode(param.node);
		}
		this.model = monacoEditorCreateModel(rawInput, StringParamLanguage.TYPESCRIPT, options.uniqueId);
		// const uri = monaco.Uri.parse('file:///main2.tsx');
		// this.model = monaco.editor.getModel(uri);
		// if (!this.model) {
		// 	this.model = monaco.editor.createModel(rawInput, 'typescript', uri);
		// } else {
		// 	this.model.setValue(rawInput);
		// }

		const editor = monaco.editor.create(element, {
			model: this.model,
			value: rawInput,
			readOnly: options.readOnly,
			// language: param.options.language(),
			...MonacoCommonOptions,
		});

		// const model = this.model;
		// let validationTimeout: number | undefined;
		// editor.onDidChangeModelContent((event) => {
		// 	clearTimeout(validationTimeout);
		// 	validationTimeout = setTimeout(() => {
		// 		monaco.languages.typescript.getTypeScriptWorker().then((worker) => {
		// 			worker(model.uri).then((client) => {
		// 				client.getSuggestionDiagnostics(model.uri.toString()).then((diagnostics) => {
		// 					console.log('getSuggestionDiagnostics:', diagnostics);
		// 				});
		// 				client.getCompilerOptionsDiagnostics(model.uri.toString()).then((diagnostics) => {
		// 					console.log('getCompilerOptionsDiagnostics:', diagnostics);
		// 				});
		// 				client.getSyntacticDiagnostics(model.uri.toString()).then((diagnostics) => {
		// 					console.log('Syntax errors:', diagnostics);
		// 				});

		// 				client.getSemanticDiagnostics(model.uri.toString()).then((diagnostics) => {
		// 					console.log('Semantic errors:', diagnostics);
		// 				});
		// 			});
		// 		});
		// 	}, 500) as any; // Wait for 500ms after user stops typing to trigger validation
		// });

		return editor;
	}

	static async setupForTypescript(
		param: StringParam,
		rawInput: string,
		element: HTMLElement,
		options: SetupForTsOptions
	) {
		return this.setupForTypescriptWithoutNode(rawInput, element, options, param);
	}

	static async addTypesForNode(node: BaseNodeType) {
		await FilesRegister.init();
		let content = await FilesRegister.polygonjsBundledTypes();

		content = this._injectNodeSpareParamsTypes(content, node);

		//
		//
		// WARNING:
		//
		// In order to get the types to work, the bundle currently require some manual editing
		//

		// content = `
		// declare class Object3D {
		// 	public position = {x:0,y:0,z:0}
		// }
		// declare class CoreGroup {
		// 	objects():Object3D[]{
		// 		return []
		// 	}
		// }
		// declare class BaseCodeSopProcessor {
		// 	initializeProcessor(){

		// 	}
		// 	cook(inputCoreGroups: CoreGroup[]){

		// 	}
		// 	setCoreGroup(coreGroup: CoreGroup){

		// 	}
		// }
		// `;
		// console.log('---------------');
		// console.log(content);
		// console.log('---------------');
		// console.log(monaco.languages.typescript.typescriptDefaults.getExtraLibs());
		const uri = 'ts:filename/facts2.d.ts';
		monaco.languages.typescript.typescriptDefaults.addExtraLib(content, uri);
		// monaco.languages.typescript.typescriptDefaults.setEagerModelSync(false);
		// https://github.com/Microsoft/monaco-editor/issues/754
		// const externalDTSFilename = 'external.d.ts';
		// monaco.languages.typescript.typescriptDefaults.addExtraLib(content, `inmemory://model/${externalDTSFilename}`);
		// https://stackoverflow.com/questions/43058191/how-to-use-addextralib-in-monaco-with-an-external-type-definition/66948535#66948535
		// monaco.languages.typescript.typescriptDefaults.addExtraLib(content);
	}

	private static _injectNodeSpareParamsTypes(content: string, node: BaseNodeType) {
		const spareParams = node.params.spare.filter((p) => p.parentParam() == null);
		const linesToAdd = spareParams.map((p) => `${p.name()}: ParamTemplate<ParamType.${p.type().toUpperCase()}>;`);
		// const b = `declare class CodeActorParamsConfig extends NodeParamsConfig {`
		// const e = `declare class CodeEventParamsConfig extends NodeParamsConfig {`
		const lineStart = `declare class Code${CoreString.titleize(
			node.context()
		)}ParamsConfig extends NodeParamsConfig {`;
		const replacement = `${lineStart}\n${linesToAdd.join('\n')}`;
		content = content.replace(lineStart, replacement);
		return content;
	}

	private static _compilerOptionsSet = false;
	static setCompilerOptions() {
		if (this._compilerOptionsSet) {
			return;
		}
		this._compilerOptionsSet = true;
		monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
			noSemanticValidation: false,
			noSyntaxValidation: false,
			// noSuggestionDiagnostics
		});

		monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
			target: monaco.languages.typescript.ScriptTarget.ES2020,
			// allowNonTsExtensions: true,
			moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
			// module: monaco.languages.typescript.ModuleKind.CommonJS,
			// noEmit: true,
			typeRoots: ['node_modules/@types'],
			//
			// allowNonTsExtensions: true,
			// strictNullChecks: true,
		});
	}

	static async transpile(model: monaco.editor.ITextModel): Promise<string | void> {
		const worker = await monaco.languages.typescript.getTypeScriptWorker();
		const client = await worker(model.uri);

		// const diagnostics = await client.getSyntacticDiagnostics(model.uri.toString());
		// console.log('Syntax errors:', diagnostics);
		// if (diagnostics.length > 0) {
		// 	return;
		// }

		const result = await client.getEmitOutput(model.uri.toString());
		return result.outputFiles[0].text;
	}
}

//
//
//
//
//
let _started = false;
async function debugExtraLib() {
	if (_started) {
		return;
	}
	_started = true;
	const extraLibs: Record<string, string> = {
		'polygonjs_bundled_types.d.ts': await FilesRegister.polygonjsBundledTypes(),
	};

	// for (const [fileName, content] of Object.entries(extraLibs)) {
	// 	monaco.languages.typescript.typescriptDefaults.addExtraLib(content, fileName);
	// 	console.log('ADD', content);
	// }

	// const editorModel = monaco.editor.createModel(
	// 	"const message: string = 'Hello, world!';\nconsole.log(message);",
	// 	'typescript'
	// );

	// let validationTimeout: number | undefined;

	// editorModel.onDidChangeContent(() => {
	// 	clearTimeout(validationTimeout);
	// 	validationTimeout = setTimeout(() => {
	// 		validateCode(editorModel);
	// 	}, 500) as any;
	// });

	// Create a model for each extra library and trigger validation when it changes
	const testContent = `
	export class CodeJsProcessor extends BaseCodeJsProcessor {}
	`;
	for (const libName of Object.keys(extraLibs)) {
		const content = true ? extraLibs[libName] : testContent;
		// console.log({content});
		const libModel = monaco.editor.createModel(content, 'typescript', monaco.Uri.file(libName));
		// libModel.onDidChangeContent(() => {
		// 	clearTimeout(validationTimeout);
		setTimeout(() => {
			validateCode(libModel);
		}, 500) as any;
		// });
	}

	function validateCode(model: monaco.editor.ITextModel) {
		// console.log('validateCode', model);

		monaco.languages.typescript.getTypeScriptWorker().then((worker) => {
			worker(model.uri).then((client) => {
				// const models = monaco.editor.getModels();
				// console.log(models);
				// models.forEach((_model) => {
				// 	console.log({model});
				// });
				// const compilerOptions = monaco.languages.typescript.typescriptDefaults.getCompilerOptions();

				// // Log the compiler options to the console
				// console.log({compilerOptions});

				// const jsExtraLibs = monaco.languages.typescript.javascriptDefaults.getExtraLibs();
				// const tsExtraLibs = monaco.languages.typescript.typescriptDefaults.getExtraLibs();
				// console.log({jsExtraLibs, tsExtraLibs});

				client.getSuggestionDiagnostics(model.uri.toString()).then((diagnostics) => {
					console.log('getSuggestionDiagnostics:', diagnostics);
				});
				client.getCompilerOptionsDiagnostics(model.uri.toString()).then((diagnostics) => {
					console.log('getCompilerOptionsDiagnostics:', diagnostics);
				});
				client.getSyntacticDiagnostics(model.uri.toString()).then((diagnostics) => {
					console.log('Syntax errors:', diagnostics);
				});

				client.getSemanticDiagnostics(model.uri.toString()).then((diagnostics) => {
					console.log('Semantic errors:', diagnostics);
				});
			});
		});
	}
}
